import React, { FunctionComponent, MouseEvent, useState } from 'react';

import classnames from 'classnames';
import { kebabCase, omit } from 'lodash';

import { RatingInput } from 'core/rating/rating-inputs/rating-input/RatingInput';
import { RatingInputsProps } from 'core/rating/types';
import {
    parseColorEmptyByValueMap,
    parseColorFullByValueMap,
    parseHtmlName,
    parseIncrement,
    parseIsReadOnly,
    parseValueMax,
    parseValueMin,
} from 'core/rating/util/Props';
import { getPotentialIntegerValues } from 'core/rating/util/Values';

import 'core/rating/rating-inputs/RatingInputs.scss';

export const RatingInputs: FunctionComponent<RatingInputsProps> = ({
    className,
    colorEmptyByValue: colorEmptyByValueFromProps,
    colorFullByValue: colorFullByValueFromProps,
    htmlName: htmlNameFromProps,
    increment: incrementFromProps,
    isReadOnly: isReadOnlyFromProps,
    potentialValues,
    selectedValue,
    ...others
}) => {
    const [hoverValue, setHoverValue] = useState<number | undefined>();
    const htmlName = parseHtmlName(htmlNameFromProps);
    const increment = parseIncrement(incrementFromProps);
    const isReadOnly = parseIsReadOnly(isReadOnlyFromProps);
    const colorEmptyByValue = parseColorEmptyByValueMap(colorEmptyByValueFromProps, potentialValues, increment);
    const colorFullByValue = parseColorFullByValueMap(colorFullByValueFromProps, potentialValues, increment);
    const getElInput = (event: MouseEvent<HTMLElement>): HTMLInputElement | null => {
        const elInputWrapper = (event.target as HTMLElement).closest('.input-wrapper');
        const elInput = elInputWrapper && (elInputWrapper.querySelector('input') as HTMLInputElement);

        return elInput;
    };
    const handleMouseMove = (event: MouseEvent<HTMLElement>): void => {
        const elInput = getElInput(event);

        if (!elInput) {
            return;
        }

        const value = Number(elInput.value);

        setHoverValue(value);
    };
    const handleMouseLeave = (): void => {
        setHoverValue(undefined);
    };

    return (
        <div
            className={classnames(
                `r4e-rating-inputs increment-${kebabCase(increment)} selected-value-${kebabCase(
                    String(selectedValue),
                )} hover-value-${kebabCase(String(hoverValue))}`,
                className,
                {
                    'state-read-only': isReadOnly,
                    'state-editable': !isReadOnly,
                },
            )}
            onMouseMove={isReadOnly ? undefined : handleMouseMove}
            onMouseLeave={isReadOnly ? undefined : handleMouseLeave}
        >
            {potentialValues.map((potentialValue) => (
                <RatingInput
                    {...others}
                    colorEmptyByValue={colorEmptyByValue}
                    colorFullByValue={colorFullByValue}
                    hoverValue={hoverValue}
                    htmlName={htmlName}
                    increment={increment}
                    isReadOnly={isReadOnly}
                    key={potentialValue}
                    selectedValue={selectedValue}
                    value={potentialValue}
                />
            ))}
        </div>
    );
};

RatingInputs.defaultProps = {
    ...omit(RatingInput.defaultProps, 'hoverValue', 'value'),
    potentialValues: getPotentialIntegerValues(parseValueMin(), parseValueMax()),
};
